// ============================================
// SociosDistribuidores — Landing "Conoce cómo unirte" (/socios-y-distribuidores)
// Página editorial que conecta con el enlace "Conoce cómo unirte" del panel de
// socio. Bandas alternadas cream/oscuro, estilo Physiology.
// Nota: algunas imágenes son las mejores disponibles del proyecto; se pueden
// reemplazar por las definitivas cuando estén.
// ============================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SolicitarAccesoModal from '../components/account/SolicitarAccesoModal';
import './SociosDistribuidores.css';

const HERO_BASE = '/images/secciones/inicio/Hero%20Carousel%20Inicio%20principal';
const UE_BASE = '/images/secciones/inicio/Inicio%20Segundo%20Hero%20Slider%20seccion%20Ultimas%20ediciones';

const IMG = {
  philosophy: `${HERO_BASE}/Physiology_skincare_site_inicio_carousel_slide_2.webp`,
  benefits: `${HERO_BASE}/Physiology_skincare_site_inicio_carousel_slide_3.webp`,
  closing: `${UE_BASE}/Inicio_seccion_Carusel_ultimas_ediciones_slide_1.webp`,
};

const REASONS = [
  {
    title: 'Fórmulas de alta calidad',
    text: 'Cada creación nace de la mano de formuladores y especialistas, con ingredientes que responden a las necesidades reales de la piel.',
  },
  {
    title: 'Innovación constante',
    text: 'Nuestra investigación nunca se detiene: I+D, iteraciones y diálogo con las tendencias globales del skincare.',
  },
  {
    title: 'Sostenibilidad y ética',
    text: 'Prácticas responsables, materiales honestos y respeto por un planeta mejor.',
  },
  {
    title: 'Un equipo que acompaña',
    text: 'Materiales de marketing, capacitación en producto y estrategias comerciales que impulsan tu crecimiento.',
  },
  {
    title: 'Éxito compartido',
    text: 'Ser parte de una comunidad que celebra logros colectivos y construye vínculos a largo plazo.',
  },
];

const TRAVEL_KITS = [
  {
    img: '/images/products/Kit_de_viaje_aura_physiology_galeria.webp',
    title: 'The Journey Kit Mini · Kit Viaje',
    text: 'Todo lo que necesita tu piel para iniciar la travesía.',
  },
  {
    img: '/images/products/Kit_de_viaje_eclipse_physiology_galeria.webp.webp',
    title: 'Eclipse · The Journey Kit · Viaje',
    text: 'Permanencia y vitalidad, listas para viajar.',
  },
  {
    img: '/images/products/Kit_de_viaje_the_odissey_physiology_texturas.webp',
    title: 'Odissea · The Journey Kit · Viaje',
    text: 'Un esencial para acompañarte en cada viaje.',
  },
];

const ASSOCIATED = [
  {
    img: '/images/products/swatches%20kit%20minimalista.webp',
    title: 'Kit Minimalista',
    text: 'Este kit contiene todo lo que necesitas.',
  },
  {
    img: '/images/secciones/Socios/Cuidado_completo.webp',
    title: 'Botanical Kit',
    text: 'Equilibrio perfecto entre naturaleza y ciencia.',
  },
];

export default function SociosDistribuidores() {
  const [accessOpen, setAccessOpen] = useState(false);

  return (
    <div className="sd" data-header-theme="light">
      <SEO
        title="Socios y Distribuidores"
        description="Únete a Physiology como socio distribuidor: fórmulas clínicas, kits de muestra, acompañamiento profesional y precios exclusivos."
        path="/socios-y-distribuidores"
      />

      {/* ── Hero / manifiesto ─────────────────────────────── */}
      <section className="sd-hero">
        <div className="sd-hero__inner">
          <h1 className="sd-hero__title">Bienvenido a Physiology — Socios y Distribuidores</h1>
          <p className="sd-hero__quote">
            «En Physiology creemos en fórmulas minimalistas, donde la ciencia y la
            belleza se encuentran en equilibrio. Convertirte en distribuidor es más
            que un negocio: es unirte a una comunidad que apuesta por la piel, la
            investigación y la sostenibilidad. Compartimos contigo no solo productos,
            sino un manifiesto de cuidado consciente y resultados reales. Tu éxito es
            nuestro viaje compartido.»
          </p>
        </div>
      </section>

      {/* ── Nuestra filosofía ─────────────────────────────── */}
      <section className="sd-philosophy">
        <div className="sd-philosophy__text">
          <h2 className="sd-h2">Nuestra filosofía</h2>
          <p>
            El cuidado de la piel nace en la ciencia y se eleva en la práctica
            profesional. Somos una marca clínica, diseñada para acompañar a médicos
            estéticos y cosmetólogos en su día a día, con fórmulas seguras,
            ingredientes probados y activos botánicos inteligentes que se adaptan a
            cada necesidad de la piel.
          </p>
          <p>
            Nuestra ideología es clara: ofrecer protocolos confiables y efectivos
            para tu consultorio, cabina o spa. Productos que no solo cuidan, sino que
            también respaldan tu labor como especialista, brindando seguridad en cada
            recomendación.
          </p>
          <div className="sd-divider" />
          <p className="sd-philosophy__note">
            <strong>Estimado distribuidor:</strong> Inicia sesión para acceder a tus
            beneficios exclusivos. Conoce ventajas cuidadosamente seleccionadas y
            accede a un espacio concebido específicamente para tu crecimiento
            profesional.
          </p>
          <button
            type="button"
            className="sd-btn"
            onClick={() => setAccessOpen(true)}
          >
            Solicitar acceso
          </button>
        </div>
        <div
          className="sd-philosophy__media"
          style={{ backgroundImage: `url('${IMG.philosophy}')` }}
          role="img"
          aria-label="Formulación Physiology"
        />
      </section>

      {/* ── Razones para unirte ───────────────────────────── */}
      <section className="sd-reasons">
        <h2 className="sd-h2 sd-reasons__title">Razones para unirte</h2>
        <div className="sd-reasons__grid">
          {REASONS.map((r) => (
            <div key={r.title} className="sd-reason">
              <h3 className="sd-reason__title">{r.title}</h3>
              <p className="sd-reason__text">{r.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Beneficios (banda oscura) ─────────────────────── */}
      <section className="sd-benefits">
        <div
          className="sd-benefits__media"
          style={{ backgroundImage: `url('${IMG.benefits}')` }}
          role="img"
          aria-label="Productos Physiology"
        />
        <div className="sd-benefits__text">
          <h2 className="sd-h2 sd-h2--light">
            Al integrarte como socio distribuidor, accedes a un universo de beneficios
            que amplifican tu práctica:
          </h2>
          <ul className="sd-benefits__list">
            <li>Kits de muestras para que tus pacientes experimenten los resultados en primera persona.</li>
            <li>Regalos exclusivos de temporada que enriquecen tu relación con los clientes.</li>
            <li>Acompañamiento y asesoría profesional constante, porque sabemos que cada piel es única y merece la mejor estrategia de tratamiento.</li>
          </ul>
        </div>
      </section>

      {/* ── Kits de regalo de temporada (intro) ───────────── */}
      <section className="sd-season">
        <h2 className="sd-h2">Kits de regalo de temporada</h2>
        <p className="sd-season__sub">
          La nueva colección inspirada en epopeyas de viaje, pensada como un gesto
          generoso para tu próxima partida. Fórmulas cotidianas y esenciales para
          acompañarte tanto en escapadas locales como en viajes alrededor del mundo.
        </p>
      </section>

      {/* ── Conceptos básicos de viaje (3 kits) ───────────── */}
      <section className="sd-collection">
        <div className="sd-collection__intro">
          <h2 className="sd-h2">Conceptos básicos de viaje</h2>
          <p>
            Diseñados para simplificar tu experiencia, nuestros kits de viaje
            contienen lo esencial para trayectos sin complicaciones, sin importar la
            distancia.
          </p>
        </div>
        <div className="sd-cards sd-cards--3">
          {TRAVEL_KITS.map((k) => (
            <article key={k.title} className="sd-card">
              <div className="sd-card__media">
                <img src={k.img} alt={k.title} loading="lazy" />
              </div>
              <h3 className="sd-card__title">{k.title}</h3>
              <p className="sd-card__text">{k.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Favoritos Asociados (2 kits) ──────────────────── */}
      <section className="sd-collection sd-collection--alt">
        <div className="sd-collection__intro">
          <h2 className="sd-h2">Favoritos Asociados</h2>
          <p>
            Conjuntos que aportan fórmulas complementarias para actuar en armonía.
            Cada kit es una invitación a experimentar un cuidado integral, con
            resultados visibles y sensoriales.
          </p>
          <Link to="/socio" className="sd-btn sd-btn--outline">Atención VIP a Distribución</Link>
        </div>
        <div className="sd-cards sd-cards--2">
          {ASSOCIATED.map((k) => (
            <article key={k.title} className="sd-card">
              <div className="sd-card__media">
                <img src={k.img} alt={k.title} loading="lazy" />
              </div>
              <h3 className="sd-card__title">{k.title}</h3>
              <p className="sd-card__text">{k.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Banda de cierre ───────────────────────────────── */}
      <section
        className="sd-closing"
        style={{ backgroundImage: `url('${IMG.closing}')` }}
        aria-hidden="true"
      />

      <SolicitarAccesoModal
        isOpen={accessOpen}
        onClose={() => setAccessOpen(false)}
      />
    </div>
  );
}
